import React, { Fragment } from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  Link,
  Typography,
} from '@one-thd/sui-atomic-components';
import { Returns, QRCode, BuyAgain, Store } from '@one-thd/sui-icons';
import {
  PTC_REFUND_TIMING_MESSAGE,
  REFUND_TIMING_MESSAGE,
} from '@src/constants';
import { useReturnStore } from '@src/stores/returnStore';
import usePrintReturnLabel from '@src/hooks/usePrintReturnLabel';
import { SummaryShipment } from '@src/models/returns';
import PostSelectionItems from '../shared/items/PostSelectionItems';
import '../../styles/returns.scss';

interface ViewUpsBarcodeDrawerProps {
  displayDrawer: boolean;
  closeDrawer: () => void;
  shipment?: SummaryShipment;
}

const ViewUpsBarcodeDrawer = ({
  displayDrawer,
  closeDrawer,
  shipment,
}: ViewUpsBarcodeDrawerProps) => {
  const { trackingDetails } = shipment || {};
  const itemsToReturn = shipment?.itemsToReturn || [];

  const isPTCOrder = useReturnStore((state) => state.isPTCOrder);

  const refundMessage = isPTCOrder
    ? PTC_REFUND_TIMING_MESSAGE
    : REFUND_TIMING_MESSAGE;

  const { onPrintClick } = usePrintReturnLabel(shipment);

  const handlePrintLabelClick = (event: React.SyntheticEvent) => {
    event.preventDefault();
    onPrintClick();
  };

  const renderUpsBarcodeImgs = () => {
    const base64decode = 'data:image/png;base64,';

    return trackingDetails?.map((trackingDetail, index) => {
      const imageSource = base64decode + trackingDetail.upsBarcodeImg;
      return (
        <Fragment key={index}>
          {trackingDetails.length !== 1 && (
            <hr data-testid="divider" className="sui-mb-4" />
          )}
          <Typography>
            Barcode {index + 1} of {trackingDetails.length}
          </Typography>
          <div className="sui-mb-4 sui-mt-4">
            <img
              className="sui-max-w-[420px] sui-w-full"
              key={index}
              src={imageSource}
              alt={`UPS Barcode ${index + 1}`}
              height={116}
              width={233}
            />
          </div>
        </Fragment>
      );
    });
  };

  return (
    <div className="sui-flex sui-flex-col sui-full sui-gap-4 sui-w-96">
      <Drawer
        open={displayDrawer}
        onClose={closeDrawer}
        position="right"
        className="drawer-z-index-fix"
      >
        <DrawerHeader title="UPS Barcode" onClose={closeDrawer} />
        <DrawerBody>
          <div className="sui-mb-4">
            <Typography>Return Items</Typography>
          </div>
          <div className="sui-flex sui-flex-row sui-justify-between sui-mb-4">
            <PostSelectionItems selectedReturnItems={itemsToReturn} />
          </div>
          <div data-testid={`barcode-imgs-size-${trackingDetails?.length}`}>
            {renderUpsBarcodeImgs()}
          </div>
          <hr className="sui-mb-4" />
          <div className="sui-mb-4 sui-flex sui-gap-3">
            <div className="sui-pt-[6px]">
              <Returns />
            </div>
            <Typography>Pack your return items in a box.</Typography>
          </div>
          <div className="sui-mb-4 sui-flex sui-gap-3">
            <div className="sui-pt-[6px]">
              <Store />
            </div>
            <Typography>
              Take the box to{' '}
              <Link
                href="https://www.theupsstore.com/tools/find-a-store"
                target="_blank"
              >
                select UPS locations
              </Link>
            </Typography>
          </div>
          <div className="sui-mb-4 sui-flex sui-gap-3">
            <div className="sui-pt-[6px]">
              <QRCode />
            </div>
            <Typography>
              Show your barcode to a UPS associate or{' '}
              <Link onClick={handlePrintLabelClick}>
                print your return label
              </Link>{' '}
              and take it to a UPS drop box location.
            </Typography>
          </div>
          <div className="sui-flex sui-gap-3">
            <div className="sui-pt-[6px]">
              <BuyAgain />
            </div>
            <Typography>{refundMessage}</Typography>
          </div>
        </DrawerBody>
      </Drawer>
    </div>
  );
};

export default ViewUpsBarcodeDrawer;
